import { useRef, useState, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppConfig } from '../../config/AppConfigContext';
import type {
  VoiceConnectionStatus,
  ConversationMessage,
  VoiceServerEvent,
} from '../../types/voice-demo';

export interface UseVoiceWebSocketReturn {
  status: VoiceConnectionStatus;
  messages: ConversationMessage[];
  conversationId: string | null;
  connect: (
    userId: string,
    companyId: string,
    conversationId: string | null,
    onReady: () => void,
    onAudioData: (data: ArrayBuffer | Blob) => void,
    onInterruption: () => void,
  ) => Promise<void>;
  disconnect: () => void;
  sendAudioData: (data: ArrayBuffer) => void;
}

export const useVoiceWebSocket = (): UseVoiceWebSocketReturn => {
  const [status, setStatus] = useState<VoiceConnectionStatus>('disconnected');
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'stop' }));
      } catch { /* ws may already be closed */ }
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  const sendAudioData = useCallback((data: ArrayBuffer) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(data);
    }
  }, []);

  const connect = useCallback(
    async (
      userId: string,
      companyId: string,
      conversationId: string | null,
      onReady: () => void,
      onAudioData: (data: ArrayBuffer | Blob) => void,
      onInterruption: () => void,
    ) => {
      // Disconnect any existing connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      setStatus('connecting');

      const token = auth0Audience
        ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
        : await getAccessTokenSilently();

      // Derive WebSocket URL: REST API is on chat.*, voice WS is on talk.*
      // e.g. https://chat.dev.service.aceup.com → wss://talk.dev.service.aceup.com
      const wsProtocol = allyApi.startsWith('https') ? 'wss:' : 'ws:';
      const apiHost = allyApi.replace(/^https?:\/\//, '').replace(/\/$/, '');
      const voiceHost = apiHost.replace(/^chat\./, 'talk.');
      const wsUrl = `${wsProtocol}//${voiceHost}/api/v1/partner/voice`;
      console.log('[VoiceWS] Connecting to:', wsUrl);

      const ws = new WebSocket(wsUrl, [`bearer-${token}`]);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      const startMessage = {
        type: 'start',
        payload: {
          userId,
          companyId,
          conversationId: conversationId || undefined,
        },
      };

      ws.onopen = () => {
        setStatus('connected');
        ws.send(JSON.stringify(startMessage));
      };

      ws.onmessage = (event: MessageEvent) => {
        // Binary data = audio
        if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {
          onAudioData(event.data);
          return;
        }

        // Text data = JSON event
        try {
          const serverEvent = JSON.parse(event.data as string) as VoiceServerEvent;
          handleServerEvent(serverEvent, onReady, onInterruption);
        } catch {
          // Unparseable message
        }
      };

      ws.onerror = (event) => {
        console.error('[VoiceWS] Error:', event);
        setStatus('disconnected');
      };

      ws.onclose = (event) => {
        console.warn('[VoiceWS] Closed — code:', event.code, 'reason:', event.reason);
        setStatus('disconnected');
        wsRef.current = null;
      };
    },
    [allyApi, auth0Audience, getAccessTokenSilently],
  );

  const handleServerEvent = useCallback(
    (event: VoiceServerEvent, onReady: () => void, onInterruption: () => void) => {
      const { type, payload } = event;

      if (type === 'partial_transcript') return;

      if (type === 'ready') {
        setStatus('recording');
        onReady();
        return;
      }

      if (type === 'final_transcript') {
        if (payload?.text) {
          const isUser = payload.source !== 'model';
          setMessages((prev) => [
            ...prev,
            {
              role: isUser ? 'human' : 'ai',
              content: payload.text!,
              created_at: new Date().toISOString(),
            },
          ]);
        }
        return;
      }

      if (type === 'interruption') {
        onInterruption();
        return;
      }

      if (type === 'end_of_turn') {
        // Turn completed, nothing to do
        return;
      }

      if (type === 'error') {
        console.error('[VoiceWS] Error:', payload?.message);
        
      }
    },
    [],
  );

  // Set initial messages when loading a conversation
  const setInitialMessages = useCallback((msgs: ConversationMessage[]) => {
    setMessages(msgs);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
      if (wsRef.current) {
        try { wsRef.current.send(JSON.stringify({ type: 'stop' })); } catch { /* */ }
        wsRef.current.close();
      }
    }, []);

  return {
    status,
    messages,
    conversationId: activeConversationId,
    connect,
    disconnect,
    sendAudioData,
  };
};
